/* eslint-disable max-lines-per-function */
import { HttpErrorResponse } from '@angular/common/http';

import { JobTemplateFull, JobTemplatesData } from '@recruitee/job-templates-types';
import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  ngrxEvent,
  pipeState,
} from '@recruitee/ngrx';

const {
  actions: defaultActions,
  namespace,
  selectors,
  reducer,
} = createEntityCollectionStore<JobTemplateFull>()('JobTemplatesFull', {
  customActions: adapter => {
    const actions = getEntityCollectionActions(adapter, ({ id }) => id!);

    return {
      fetchOne: (state, payload: { id: number; langCode?: string }) =>
        adapter.markAsPending(state, { scope: null, entities: [payload.id] }),
      fetchOneSuccess: (state, payload: { id: number; offerTemplate: JobTemplateFull }) =>
        pipeState(
          state,
          next => adapter.upsertOne(next, payload.offerTemplate),
          next => adapter.markAsLoaded(next, { scope: null, entities: [payload.id] }),
        ),
      fetchOneFailed: (state, payload: { id: number; error: HttpErrorResponse }) =>
        adapter.setError(state, payload.error, { scope: null, entities: [payload.id] }),
      createOnePessimistic: (
        state,
        payload: {
          correlationId: string;
          data: {
            name: string;
            primaryLangCode?: string;
          };
        },
      ) => adapter.markAsPending(state),
      updateOne: (state, payload: { offerTemplate: JobTemplatesData & { id: number } }) =>
        pipeState(
          adapter.markAsPending(state, { scope: null, entities: [payload.offerTemplate.id] }),
        ),
      updateOneSuccess: (state, payload: { id: number; data: JobTemplateFull }) =>
        pipeState(
          state,
          next => adapter.updateOne(next, payload.data),
          next => adapter.markAsLoaded(next, { scope: null, entities: [payload.id] }),
        ),
      updateOneFailed: (
        state,
        payload: { id: number; error: HttpErrorResponse & { errorFields: any } },
      ) =>
        pipeState(adapter.setError(state, payload.error, { scope: null, entities: [payload.id] })),
      updateName: (state, payload: { data: { id: number; name: string } }) =>
        adapter.markAsPending(state, { scope: null, entities: [payload.data.id] }),
      updateNameSuccess: (state, payload: { data: JobTemplateFull }) =>
        pipeState(
          state,
          next => {
            const entity = { ...next.entityMap[payload.data.id] };
            return adapter.updateOne(next, { ...entity, name: payload.data.name });
          },
          next => adapter.markAsLoaded(next, { entities: [payload.data.id], scope: null }),
        ),
      updateNameFailed: actions.updateOnePessimisticFailed,
      duplicate: ngrxEvent<{ id: number }>(),
      duplicateSuccess: (state, payload: { offerTemplate: JobTemplateFull }) =>
        adapter.addOne(state, payload.offerTemplate),
      duplicateFailed: ngrxEvent<{
        id: number;
        error: HttpErrorResponse & { errorFields: any };
      }>(),
      delete: actions.removeOnePessimistic,
      deleteSuccess: actions.removeOnePessimisticSuccess,
      deleteFailed: actions.removeOnePessimisticFailed,
      uploadCoverImage: ngrxEvent<{ id: number; file: File }>(),
      uploadCoverImageSuccess: ngrxEvent<{ fileName: string; url: string }>(),
      uploadCoverImageFailed: ngrxEvent<{
        id: number;
        error: HttpErrorResponse;
      }>(),
    };
  },
});

const actions = {
  fetchOne: defaultActions.fetchOne,
  fetchOneFailed: defaultActions.fetchOneFailed,
  fetchOneSuccess: defaultActions.fetchOneSuccess,
  createOnePessimistic: defaultActions.createOnePessimistic,
  createOnePessimisticSuccess: defaultActions.createOnePessimisticSuccess,
  createOnePessimisticFailed: defaultActions.createOnePessimisticFailed,
  updateOne: defaultActions.updateOne,
  updateOneSuccess: defaultActions.updateOneSuccess,
  updateOneFailed: defaultActions.updateOneFailed,
  updateName: defaultActions.updateName,
  updateNameSuccess: defaultActions.updateNameSuccess,
  updateNameFailed: defaultActions.updateNameFailed,
  duplicate: defaultActions.duplicate,
  duplicateSuccess: defaultActions.duplicateSuccess,
  duplicateFailed: defaultActions.duplicateFailed,
  delete: defaultActions.delete,
  deleteSuccess: defaultActions.deleteSuccess,
  deleteFailed: defaultActions.deleteFailed,
  uploadCoverImage: defaultActions.uploadCoverImage,
  uploadCoverImageSuccess: defaultActions.uploadCoverImageSuccess,
  uploadCoverImageFailed: defaultActions.uploadCoverImageFailed,
};

export {
  actions as JobTemplatesFullActions,
  selectors as JobTemplatesFullSelectors,
  namespace as JobTemplatesFullNamespace,
  reducer as JobTemplatesFullReducer,
};
